import './style.scss';

import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import clsx from 'clsx';
import React, { useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import Checkbox from '@/src/components/common/Checkbox/Checkbox';
import { MyPortfolioColumnsEnum } from '@/src/components/myTasks/MyPortfolio/types';
import { useMyPortfolio } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/store/myPortfolio.store';
import { useToggle } from '@/src/hooks/useToggle';

import { MyPortfolioColumns } from '../../Table/Table';

const Arranger = () => {
  const { columns, toggleColumnEnabled, updateColumns } = useMyPortfolio();
  const [showPopper, toggleShowPopper] = useToggle(false);
  const popperRef = useRef();

  const columnCount =
    Object.keys(columns).filter((key) => columns[key].default && !columns[key].enabled).length +
    Object.keys(columns).filter((key) => !columns[key].default && columns[key].enabled).length;

  const reorder = useCallback((list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  }, []);

  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const items = reorder(Object.keys(columns), result.source.index, result.destination.index);
      const updatedList = items.reduce<MyPortfolioColumns>((acc, item: MyPortfolioColumnsEnum) => {
        return { ...acc, [item]: columns[item] };
      }, {});
      updateColumns(updatedList as MyPortfolioColumns);
    },
    [updateColumns, reorder, columns]
  );

  const handleChange = useCallback(
    (key) => () => {
      toggleColumnEnabled(key);
    },
    [toggleColumnEnabled]
  );

  return (
    <React.Fragment>
      <a
        className={clsx('tw-bg-white', showPopper && 'active')}
        onClick={toggleShowPopper}
        ref={popperRef}
      >
        <i className="material-icons">more_horiz</i>
        {columnCount !== 0 && (
          <span className="columnsCounter">{columnCount * (columnCount / columnCount)}</span>
        )}
      </a>
      <Popper
        id="columnArranger"
        open={showPopper}
        anchorEl={popperRef.current}
        placement="bottom-end"
        style={{ zIndex: 12 }}
      >
        <ClickAwayListener onClickAway={() => toggleShowPopper(false)}>
          <div className="arranger">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <div
                    className={`droppableContainer ${
                      snapshot.isDraggingOver ? 'itemDragging' : ''
                    }`}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {Object.keys(columns).map((key, i) => {
                      const column = columns[key];

                      if (!column.readOnly) {
                        return (
                          <Draggable key={`columnSortingKey_${key}`} draggableId={key} index={i}>
                            {(provided, snapshot) => (
                              <div
                                className={`columnItem ${snapshot.isDragging ? 'dragging' : ''}`}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <div className="columnDraggable">
                                  <i className="material-icons">drag_indicator</i>
                                </div>
                                <div className="columnAction">
                                  <Checkbox
                                    checked={column.enabled}
                                    onChange={handleChange(key)} // Pass key to handleChange
                                  />
                                  <span>{column.name}</span>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      }
                      return null;
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </ClickAwayListener>
      </Popper>
    </React.Fragment>
  );
};

export default Arranger;
